/* ==========================================================================
   PAW POWER — MOTION LAYER

   Pairs with assets/pp-anim.css.

   Two jobs:

   1. Run the animations for anything marked up with the data attributes
      (data-pp-reveal / split / parallax / count / marquee).

   2. AUTO-MARK the sections that were built before this file existed. The
      store has ~25 custom sections and the theme's own grids; tagging each
      one by hand would mean editing every template and would go stale the
      next time a section is added. Instead the map below tags by the class
      names the design system already uses, so a new section written to the
      same conventions animates for free.

   Everything is opt-out: an element that already carries data-pp-reveal is
   left alone, and prefers-reduced-motion short-circuits the whole file.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function $$(s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); }

  /* ======================================================================
     Auto-marking

     [selector, effect, stagger?]
       stagger true  -> each match gets an increasing --pp-i within its parent
     ====================================================================== */
  var AUTO = [
    /* section headers, in reading order */
    ['.PP__Eyebrow', 'up', false],
    ['.PP__Title', 'split', false],
    ['.PP__Sub', 'up', false],
    ['.PP__Actions', 'up', false],

    /* hero */
    ['.PP-Hero__Kicker', 'up', false],
    ['.PP-Hero__Title', 'split', false],
    ['.PP-Hero__Text', 'up', false],
    ['.PP-Hero__Trust', 'up', false],

    /* card and tile grids — these are the ones that want a stagger */
    ['.PP-Props__Item', 'up', true],
    ['.PP-Cols__Card', 'up', true],
    ['.PP-Card', 'up', true],
    ['.PP-Awards__Item', 'up', true],
    ['.PP-Press__Item', 'fade', true],
    ['.PP-Faq__Item', 'up', true],
    ['.PP-Dose__Row', 'up', true],

    /* two-column blocks come in from their own side */
    ['.PP-IT__Media', 'left', false],
    ['.PP-IT__Body', 'right', false],

    /* NOT the theme's product grid. Prestige stages those itself: its JS sets
       visibility:hidden inline on every .ProductItem and then animates it in.
       Marking them here as well put two entrance systems on one element, and
       when Prestige's half did not finish the card stayed invisible - which
       blanked the whole collection page. Prestige owns that grid; see
       initProductItemSafety below for the backstop. */

    /* product page buy box */
    ['.PP-Tier', 'up', true],

    /* comparison table and guarantee panel read better as one piece */
    ['.PP-Compare__Scroll', 'scale', false],
    ['.PP-Guarantee__Layout', 'up', false]
  ];

  function autoMark() {
    AUTO.forEach(function (rule) {
      var sel = rule[0], effect = rule[1], stagger = rule[2];
      var seen = typeof Map === 'function' ? new Map() : null;
      var counters = [];

      $$(sel).forEach(function (el) {
        if (el.hasAttribute('data-pp-reveal') || el.hasAttribute('data-pp-split')) return;

        /* Never animate a theme product card. Every card carries both
           .ProductItem and .PP-Card, so the '.PP-Card' rule above was pulling
           the whole collection grid into the reveal system by the back door -
           even though .ProductItem was removed from this list on purpose.
           That left two entrance animations racing on one element (Prestige's
           and this one); when this one lost, the card sat at opacity 0 for
           good and products simply vanished from the shop page. */
        if (el.classList.contains('ProductItem')) return;

        if (effect === 'split') {
          el.setAttribute('data-pp-split', '');
          return;
        }
        el.setAttribute('data-pp-reveal', effect);

        if (!stagger) return;

        /* Index within the element's own parent, so two grids on one page
           each start their stagger at zero rather than the second grid
           inheriting a long delay from the first. */
        var parent = el.parentNode;
        var idx;
        if (seen) {
          idx = seen.get(parent) || 0;
          seen.set(parent, idx + 1);
        } else {
          var slot = null;
          for (var i = 0; i < counters.length; i++) {
            if (counters[i].p === parent) { slot = counters[i]; break; }
          }
          if (!slot) { slot = { p: parent, n: 0 }; counters.push(slot); }
          idx = slot.n++;
        }
        /* Cap the delay: a 20-product grid should not take two seconds to
           finish arriving. */
        el.style.setProperty('--pp-i', Math.min(idx, 7));
      });
    });
  }

  /* ======================================================================
     Split headlines into words

     Only plain-text headings are split. Anything containing markup is left
     alone and revealed as a block, because rebuilding its innerHTML would
     drop links, <br> and highlight spans.
     ====================================================================== */
  function splitText() {
    $$('[data-pp-split]').forEach(function (el) {
      if (el.__ppSplit) return;
      el.__ppSplit = true;

      var hasMarkup = el.children.length > 0;
      if (hasMarkup) {
        el.setAttribute('data-pp-reveal', 'up');
        el.removeAttribute('data-pp-split');
        return;
      }

      var words = (el.textContent || '').trim().split(/\s+/);
      if (!words.length || words.length > 40) {
        el.setAttribute('data-pp-reveal', 'up');
        el.removeAttribute('data-pp-split');
        return;
      }

      var html = '';
      words.forEach(function (w, i) {
        html += '<span class="PP-Split__Word"><span class="PP-Split__Inner" style="--pp-i:' + i + '">' +
                w.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') +
                '</span></span>';
        if (i < words.length - 1) html += ' ';
      });
      el.innerHTML = html;
    });
  }

  /* ======================================================================
     Reveal on arrival
     ====================================================================== */
  function initReveal() {
    var targets = $$('[data-pp-reveal], [data-pp-split]');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.06 });

    targets.forEach(function (el) {
      /* Anything already on screen at load reveals immediately rather than
         animating in behind the fold-line the observer would use. */
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.9 && rect.bottom > 0) {
        el.classList.add('is-revealed');
        return;
      }
      io.observe(el);
    });
  }

  /* ======================================================================
     Count-up

     <span data-pp-count="250000" data-pp-suffix="+">0</span>
     ====================================================================== */
  function initCounters() {
    var els = $$('[data-pp-count]');
    if (!els.length || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        io.unobserve(el);

        var target = parseFloat(el.getAttribute('data-pp-count')) || 0;
        var prefix = el.getAttribute('data-pp-prefix') || '';
        var suffix = el.getAttribute('data-pp-suffix') || '';
        var dur = parseInt(el.getAttribute('data-pp-duration'), 10) || 1400;
        var start = null;

        function frame(ts) {
          if (start === null) start = ts;
          var p = Math.min(1, (ts - start) / dur);
          /* ease-out cubic, so it decelerates into the final number */
          var eased = 1 - Math.pow(1 - p, 3);
          var value = Math.round(target * eased);
          el.textContent = prefix + value.toLocaleString() + suffix;
          if (p < 1) requestAnimationFrame(frame);
        }
        requestAnimationFrame(frame);
      });
    }, { threshold: 0.4 });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ======================================================================
     Marquee

     The animation slides one track by exactly its own width, so the loop is
     seamless only while a single track is at least as wide as the container.
     A short row - five short announcement messages on a wide desktop - is
     not, and the result is a stretch of empty bar drifting past.

     So the content is repeated until one track overflows the container, and
     only then duplicated for the loop. Re-measured on resize, because what
     overflows a phone does not overflow a desktop.
     ====================================================================== */
  function initMarquee() {
    $$('[data-pp-marquee]').forEach(function (el) {
      if (el.__ppMarquee) return;
      el.__ppMarquee = true;

      var track = el.querySelector('.PP-Marquee__Track');
      if (!track) return;

      /* Keep a pristine copy of the original items to rebuild from. */
      var seed = track.cloneNode(true);

      function build() {
        // reset to a single pass
        el.querySelectorAll('.PP-Marquee__Track').forEach(function (t, i) {
          if (i > 0) t.remove();
        });
        var first = el.querySelector('.PP-Marquee__Track');
        first.innerHTML = seed.innerHTML;

        var need = el.clientWidth;
        /* Laid out yet? If the bar has no width the measurements are
           meaningless — bail and let the load / fonts hooks below run it
           again, rather than leaving a single un-cloned track behind. */
        if (!need) return;

        /* Repeat the items until one track is wider than the bar. Capped so
           a very short message on a very wide screen cannot loop forever. */
        var guard = 0;
        while (first.scrollWidth < need && guard < 20) {
          first.insertAdjacentHTML('beforeend', seed.innerHTML);
          guard++;
        }

        /* Constant pixel speed instead of a constant loop time.

           A fixed "seconds per loop" means the bar crawls on a phone and
           races on a desktop, because the same duration covers very
           different distances. Deriving the duration from the track's real
           width keeps it moving at the same pace on every screen. */
        var pps = parseFloat(el.getAttribute('data-pp-marquee-pps')) || 0;
        if (pps > 0) {
          var seconds = first.scrollWidth / pps;
          first.style.animationDuration = seconds + 's';
        }

        var clone = first.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        el.appendChild(clone);
      }

      build();

      /* Re-measure once images and webfonts have landed: both change the
         track's width, and the loop is only seamless when the width the
         animation was sized against is the final one. */
      window.addEventListener('load', build);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(build).catch(function () {});
      }

      var t;
      window.addEventListener('resize', function () {
        clearTimeout(t);
        t = setTimeout(build, 200);
      }, { passive: true });
    });
  }

  /* ======================================================================
     Parallax

     One scroll listener for every element, coalesced into a rAF. Elements
     off screen are skipped so a long page does not pay for its whole length
     on every frame.
     ====================================================================== */
  function initParallax() {
    var els = $$('[data-pp-parallax]');
    if (!els.length) return;

    var ticking = false;

    function update() {
      ticking = false;
      var vh = window.innerHeight;

      els.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;

        var speed = parseFloat(el.getAttribute('data-pp-parallax')) || 0.15;
        /* -1 at the top of the viewport, +1 at the bottom */
        var progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2);
        el.style.setProperty('--pp-shift', (progress * speed * 100).toFixed(1) + 'px');
      });
    }

    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* ======================================================================
     Header — retreat on the way down, return on the way up

     Only once past the fold, and never while a drawer or modal is open, so
     it cannot pull the header out from under an open menu.
     ====================================================================== */
  function initHeader() {
    var header = document.querySelector('.Header');
    if (!header) return;

    var last = window.pageYOffset;
    var ticking = false;

    function update() {
      ticking = false;
      var y = window.pageYOffset;
      var open = document.documentElement.classList.contains('is-scroll-locked') ||
                 document.body.classList.contains('is-scroll-locked') ||
                 document.querySelector('.Drawer.is-open, .Modal.is-open');

      if (open || y < 300) {
        header.classList.remove('pp-hidden');
      } else if (y > last + 6) {
        header.classList.add('pp-hidden');
      } else if (y < last - 6) {
        header.classList.remove('pp-hidden');
      }
      last = y;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
  }

  /* ======================================================================
     Boot
     ====================================================================== */
  /* ----------------------------------------------------------------------
     Product grid backstop

     Prestige hides every .ProductItem inline and relies on its own staggering
     to bring it back. If that never runs - script error, an observer that
     does not fire, a tab restored from bfcache - the grid is silently blank
     and the shop looks broken.

     Nothing about a product grid is worth that risk, so anything still hidden
     shortly after load is simply shown.
     ---------------------------------------------------------------------- */
  function initProductItemSafety() {
    function unhide() {
      $$('.ProductItem').forEach(function (el) {
        if (el.style.visibility === 'hidden') el.style.visibility = '';
        /* The reveal system hides with opacity, not visibility, so clearing
           visibility alone could never rescue a card it had caught. */
        if (el.hasAttribute('data-pp-reveal')) el.classList.add('is-revealed');
      });
    }
    setTimeout(unhide, 1200);
    window.addEventListener('load', function () { setTimeout(unhide, 400); });
    /* Coming back via the back button restores the hidden inline style. */
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) setTimeout(unhide, 200);
    });
  }

  function boot() {
    /* Read by the inline head script's failsafe: if this never flips, the
       resting styles are removed and the page shows normally. */
    window.__ppAnimBooted = true;

    if (reduced) {
      /* Still build the marquee markup so the band is not half-empty, but
         the stylesheet has already stopped it moving. */
      initMarquee();
      initProductItemSafety();
      $$('[data-pp-reveal], [data-pp-split]').forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    autoMark();
    splitText();
    initReveal();
    initCounters();
    initMarquee();
    initParallax();
    initHeader();
    initProductItemSafety();
  }

  /* The stylesheet only hides things once .pp-anim-ready is on <html>, and
     that class is set by an inline script in layout/theme.liquid which also
     carries the failsafe that removes it if this file never boots. */
  if (reduced) root.classList.remove('pp-anim-ready');

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Sections re-rendered by the theme editor need re-marking. */
  document.addEventListener('shopify:section:load', function () {
    autoMark();
    splitText();
    initReveal();
    initMarquee();
  });
})();
